import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { AxiosError, AxiosInstance } from 'axios';
import { Jwt } from '@thingslog/repositories';
import { AuthContext } from './AuthContext';
import {
  DEFAULT_CSRF_COOKIE_NAME,
  DEFAULT_CSRF_FIELD_NAME,
  DEFAULT_LOGIN_URL,
  DEFAULT_LOGOUT_URL,
  DEFAULT_ME_URL,
  LOGIN_ATTEMPT_KEY,
  LOGIN_RETRY_WINDOW_MS,
  POST_LOGIN_PATH_KEY,
  getCurrentPath,
  submitSessionLogout,
} from './session';

interface SessionAuthProviderProps {
  Axios: AxiosInstance;
  meUrl?: string;
  loginUrl?: string;
  logoutUrl?: string;
  csrfCookieName?: string;
  csrfFieldName?: string;
  onSuccessfulAuthentication?: (user: Jwt) => void;
  onFailedAuthentication?: () => void;
  children: ReactNode;
}

/**
 * Session based counterpart of AuthProvider. The browser holds no token - authentication lives in
 * the server side session cookie, so the only thing the SPA does is ask the backend who it is and
 * redirect to the backend initiated Keycloak login when it has no session.
 */
export const SessionAuthProvider: FC<SessionAuthProviderProps> = ({
  Axios,
  meUrl = DEFAULT_ME_URL,
  loginUrl = DEFAULT_LOGIN_URL,
  logoutUrl = DEFAULT_LOGOUT_URL,
  csrfCookieName = DEFAULT_CSRF_COOKIE_NAME,
  csrfFieldName = DEFAULT_CSRF_FIELD_NAME,
  onSuccessfulAuthentication,
  onFailedAuthentication,
  children,
}: SessionAuthProviderProps) => {
  const [user, setUser] = useState<Jwt | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const isRun = useRef<boolean>(false);

  /** Returns whether the browser is now navigating to the login page. */
  const redirectToLogin = (): boolean => {
    const lastAttempt = Number(sessionStorage.getItem(LOGIN_ATTEMPT_KEY) ?? 0);

    // A backend that keeps answering 401 right after a login round trip would otherwise bounce the
    // browser between the SPA and Keycloak forever.
    if (lastAttempt && Date.now() - lastAttempt < LOGIN_RETRY_WINDOW_MS) {
      console.error('Suppressing login redirect - the session is still not established');
      return false;
    }

    sessionStorage.setItem(LOGIN_ATTEMPT_KEY, String(Date.now()));
    sessionStorage.setItem(POST_LOGIN_PATH_KEY, getCurrentPath());
    window.location.href = loginUrl;
    return true;
  };

  const login = (): void => {
    redirectToLogin();
  };

  const logout = (): void => submitSessionLogout({ logoutUrl, csrfCookieName, csrfFieldName });

  const restorePostLoginPath = (): boolean => {
    const postLoginPath = sessionStorage.getItem(POST_LOGIN_PATH_KEY);
    sessionStorage.removeItem(POST_LOGIN_PATH_KEY);

    if (postLoginPath && postLoginPath !== getCurrentPath()) {
      window.location.replace(postLoginPath);
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (isRun.current) return;
    isRun.current = true;

    const loadSession = async (): Promise<void> => {
      // Stay on the loading screen while the browser navigates away. Rendering the app meanwhile
      // lets it start a navigation of its own (portal's BasePage sends a missing user to /app),
      // which cancels this one.
      let isLeavingPage = false;
      try {
        const response = await Axios.get<Jwt>(meUrl, { withCredentials: true });
        sessionStorage.removeItem(LOGIN_ATTEMPT_KEY);
        setUser(response.data);
        onSuccessfulAuthentication?.(response.data);
        isLeavingPage = restorePostLoginPath();
      } catch (error: unknown) {
        setUser(null);
        onFailedAuthentication?.();

        if ((error as AxiosError).response?.status === 401) {
          isLeavingPage = redirectToLogin();
          return;
        }
        console.error('Loading the session failed', error);
      } finally {
        if (!isLeavingPage) {
          setIsInitialized(true);
        }
      }
    };

    loadSession();
  }, []);

  if (!isInitialized) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: user !== null,
        isInitialized,
        token: undefined,
        user,
        login,
        logout,
        updateToken: (): Promise<{ success: boolean; token: string | null }> =>
          Promise.resolve({ success: false, token: null }),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
